import { useMemo, useState } from 'react'
import { usePersonasList } from '@/api/queries'

interface Edge { a: string; b: string; state: 'mutual' | 'refused' | 'oneway' }

/**
 * Computes the directed addressee graph from the loaded persona front-matter.
 * - mutual: A→B AND B→A both permit
 * - refused: one side names the other as addressee but the other doesn't accept
 * - oneway: A permits B but B is silent (informational, not refused)
 */
export function computeEdges(personas: Array<{ role: string; frontMatter: any }>): Edge[] {
  const can = (a: any, b: any) =>
    (a.allowed_addressees ?? []).includes(b.role) && (b.allowed_addressers ?? []).includes(a.role)
  const wants = (a: any, b: any) => (a.allowed_addressees ?? []).includes(b.role)
  const edges: Edge[] = []
  for (let i = 0; i < personas.length; i++) {
    for (let j = i + 1; j < personas.length; j++) {
      const A = { role: personas[i].role, ...personas[i].frontMatter }
      const B = { role: personas[j].role, ...personas[j].frontMatter }
      const aToB = can(A, B), bToA = can(B, A)
      const aWantsB = wants(A, B), bWantsA = wants(B, A)
      let state: Edge['state'] | null = null
      if (aToB && bToA) state = 'mutual'
      else if ((aWantsB && !aToB) || (bWantsA && !bToA)) state = 'refused'
      else if (aToB || bToA) state = 'oneway'
      if (state) edges.push({ a: A.role, b: B.role, state })
    }
  }
  return edges
}

export function Topology() {
  const q = usePersonasList()
  const [viz, setViz] = useState<'radial' | 'matrix'>('radial')
  const personas = q.data?.items ?? []
  const edges = useMemo(() => computeEdges(personas), [personas])
  const refused = edges.filter((e) => e.state === 'refused').length

  if (q.isLoading) return <div className="page"><p>Loading topology…</p></div>

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Topology</h1>
          <p>{personas.length} personas · {edges.length} edges · <b style={{ color: refused > 0 ? 'var(--err)' : 'var(--ok)' }}>{refused} refused</b></p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className={`btn ${viz === 'radial' ? 'primary' : ''}`} onClick={() => setViz('radial')}>Radial</button>
          <button className={`btn ${viz === 'matrix' ? 'primary' : ''}`} onClick={() => setViz('matrix')}>Matrix</button>
          <button className="btn" onClick={() => exportDot(edges)}>DOT</button>
          <button className="btn" onClick={() => exportMermaid(edges)}>Mermaid</button>
        </div>
      </div>
      {viz === 'radial' ? <RadialGraph personas={personas} edges={edges} /> : <MatrixView personas={personas} edges={edges} />}
    </div>
  )
}

function RadialGraph({ personas, edges }: { personas: any[]; edges: Edge[] }) {
  const W = 720, H = 480, cx = W / 2, cy = H / 2, R = 180
  const node = (i: number) => {
    const ang = (i / personas.length) * Math.PI * 2 - Math.PI / 2
    return { x: cx + R * Math.cos(ang), y: cy + R * Math.sin(ang) }
  }
  const at = (role: string) => personas.findIndex((p) => p.role === role)
  const stroke = (s: Edge['state']) => s === 'mutual' ? 'var(--ok)' : s === 'refused' ? 'var(--err)' : 'var(--stroke-strong)'
  return (
    <svg width={W} height={H} role="img" aria-label="Persona addressee graph">
      {edges.map((e) => {
        const a = node(at(e.a)), b = node(at(e.b))
        return <line key={`${e.a}-${e.b}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke(e.state)} strokeDasharray={e.state === 'refused' ? '6 4' : ''} strokeWidth={1.5} />
      })}
      {personas.map((p, i) => {
        const n = node(i)
        return (
          <g key={p.role}>
            <circle cx={n.x} cy={n.y} r={14} fill="var(--bg-surface)" stroke="var(--brand-solid)" />
            <text x={n.x} y={n.y + 28} textAnchor="middle" fontSize={11} fontFamily="var(--font-mono)" fill="var(--fg-1)">{p.role}</text>
          </g>
        )
      })}
    </svg>
  )
}

function MatrixView({ personas, edges }: { personas: any[]; edges: Edge[] }) {
  const cellFor = (a: string, b: string): { sym: string; color: string } => {
    const e = edges.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a))
    if (!e) return { sym: '·', color: 'var(--fg-3)' }
    if (e.state === 'mutual') return { sym: '✓', color: 'var(--ok)' }
    if (e.state === 'refused') return { sym: '✕', color: 'var(--err)' }
    return { sym: '→', color: 'var(--fg-2)' }
  }
  return (
    <table className="matrix">
      <thead><tr><th></th>{personas.map((p) => <th key={p.role}>{p.role}</th>)}</tr></thead>
      <tbody>
        {personas.map((row) => (
          <tr key={row.role}>
            <th>{row.role}</th>
            {personas.map((col) => {
              if (row.role === col.role) return <td key={col.role} className="muted">—</td>
              const c = cellFor(row.role, col.role)
              return <td key={col.role} style={{ color: c.color, textAlign: 'center' }}>{c.sym}</td>
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function exportDot(edges: Edge[]) {
  const lines = ['digraph topology {', '  rankdir=LR;']
  for (const e of edges) {
    const style = e.state === 'refused' ? ' [style=dashed,color="red"]' : e.state === 'mutual' ? ' [dir=both,color="green"]' : ''
    lines.push(`  "${e.a}" -> "${e.b}"${style};`)
  }
  lines.push('}')
  download('fusion-topology.dot', lines.join('\n'))
}

function exportMermaid(edges: Edge[]) {
  const lines = ['graph LR']
  for (const e of edges) {
    const op = e.state === 'refused' ? '-.->|refused|' : e.state === 'mutual' ? '<-->' : '-->'
    lines.push(`  ${e.a} ${op} ${e.b}`)
  }
  download('fusion-topology.mmd', lines.join('\n'))
}

function download(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
